import type { BiometricsThresholdRequest } from "./biometrics-threshold.model";

/** Measurement types that have configurable alert thresholds */
export type AlertThresholdMeasurementType = "BLOOD_PRESSURE" | "GLUCOSE";

/** SubType values used in /api/biometrics/limits (same as BiometricType for these) */
const LIMITS_SUBTYPE = {
	GLUCOSE: "GLUCOSE",
	BLOOD_PRESSURE_SYSTOLIC: "BLOOD_PRESSURE_SYSTOLIC",
	BLOOD_PRESSURE_DIASTOLIC: "BLOOD_PRESSURE_DIASTOLIC",
} as const;

const FALLBACK_VALUE = "-";

/**
 * Build default alert threshold rows from /api/biometrics/limits.
 * Uses critical-severity limits only. On missing or invalid data, shows "-" (fail silently).
 */
export function buildDefaultAlertThresholdsFromLimits(
	limits: BiometricsThresholdRequest[] | undefined
): Omit<ParticipantAlertThresholdRow, "isCustom">[] {
	try {
		const critical = (limits ?? []).filter((l) => l.severity === "critical");
		const bySubType = new Map(critical.map((l) => [l.subType, l]));

		const glucoseLimit = bySubType.get(LIMITS_SUBTYPE.GLUCOSE);
		const sysLimit = bySubType.get(LIMITS_SUBTYPE.BLOOD_PRESSURE_SYSTOLIC);
		const diaLimit = bySubType.get(LIMITS_SUBTYPE.BLOOD_PRESSURE_DIASTOLIC);

		const bloodPressureHigh =
			sysLimit?.maxValue != null && diaLimit?.maxValue != null
				? `${sysLimit.maxValue}/${diaLimit.maxValue}`
				: FALLBACK_VALUE;
		const bloodPressureLow =
			sysLimit?.minValue != null && diaLimit?.minValue != null
				? `${sysLimit.minValue}/${diaLimit.minValue}`
				: FALLBACK_VALUE;

		const glucoseHigh =
			glucoseLimit?.maxValue != null ? String(glucoseLimit.maxValue) : FALLBACK_VALUE;
		const glucoseLow =
			glucoseLimit?.minValue != null ? String(glucoseLimit.minValue) : FALLBACK_VALUE;

		return [
			{
				measurementType: "BLOOD_PRESSURE",
				measurementLabelKey: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.BLOOD_PRESSURE",
				criticallyHigh: bloodPressureHigh,
				criticallyLow: bloodPressureLow,
			},
			{
				measurementType: "GLUCOSE",
				measurementLabelKey: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.GLUCOSE",
				criticallyHigh: glucoseHigh,
				criticallyLow: glucoseLow,
			},
		];
	} catch {
		return [
			{
				measurementType: "BLOOD_PRESSURE",
				measurementLabelKey: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.BLOOD_PRESSURE",
				criticallyHigh: FALLBACK_VALUE,
				criticallyLow: FALLBACK_VALUE,
			},
			{
				measurementType: "GLUCOSE",
				measurementLabelKey: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.GLUCOSE",
				criticallyHigh: FALLBACK_VALUE,
				criticallyLow: FALLBACK_VALUE,
			},
		];
	}
}

export interface ParticipantAlertThresholdRow {
	/** Backend entity ids when row is customized (one per threshold value, for PUT update) */
	id?: number;
	ids?: number[];
	measurementType: AlertThresholdMeasurementType;
	/** Display label key e.g. PARTICIPANT.BIOMETRICS.ALERT_VALUES.BLOOD_PRESSURE */
	measurementLabelKey: string;
	/** Critically high value - display string e.g. "180/120" for BP or "400" for glucose */
	criticallyHigh: string;
	/** Critically low value - display string e.g. "90/60" for BP or "60" for glucose */
	criticallyLow: string;
	/** True if this row has been customized for the patient */
	isCustom: boolean;
}
